import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Settings2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePeriodeStore } from "@/hooks/use-periode";
import {
  getMasterKonstanta,
  putMasterKonstanta,
  type MasterKonstantaItem,
} from "@/pages/common/remun-dosen/api/master-konstanta";

export function ConfigKonstantaDialog() {
  const [open, setOpen] = useState(false);
  const [konstantaData, setKonstantaData] = useState<MasterKonstantaItem[]>([]);
  const queryClient = useQueryClient();
  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester } = getYearAndSemester();

  const { data, isLoading } = useQuery({
    queryKey: ["master-konstanta", year, semester],
    queryFn: () => getMasterKonstanta(year, semester),
    enabled: open && !!year && !!semester,
  });

  useEffect(() => {
    if (data) {
      setKonstantaData(data);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (data: MasterKonstantaItem[]) =>
      putMasterKonstanta(data, year, semester),
    onSuccess: () => {
      toast.success("Master Konstanta berhasil disimpan");
      queryClient.invalidateQueries({ queryKey: ["master-konstanta"] });
      setOpen(false);
    },
    onError: () => {
      toast.error("Gagal menyimpan Master Konstanta");
    },
  });

  const handleSave = () => {
    mutation.mutate(konstantaData);
  };

  const handleChange = (fungsional: string, value: string) => {
    const val = parseFloat(value) || 0;
    setKonstantaData((prev) =>
      prev.map((item) =>
        item.fungsional === fungsional ? { ...item, konstanta: val } : item,
      ),
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="font-semibold shadow-sm ml-2">
          <Settings2 className="w-4 h-4 mr-2" />
          Config Konstanta
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Konfigurasi Master Konstanta</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {konstantaData.map((item) => (
                <div key={item.fungsional} className="space-y-2">
                  <Label>Jabatan: {item.fungsional}</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.1}
                    value={item.konstanta}
                    onChange={(e) =>
                      handleChange(item.fungsional, e.target.value)
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Batal
          </Button>
          <Button
            onClick={handleSave}
            disabled={mutation.isPending || isLoading}
          >
            {mutation.isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            Simpan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
